<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="charge-code-main">
    <div>
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>
    <div class="table-box-container">
      <TableBox
        title="Charge Code"
        :tableData="chargeCodeListMaster"
        :maxSelection="1"
        :selectedRow.sync="chargeCodeTableBoxSelectedRow"
        :loading="chargeCodeTableBoxLoading"
      />
    </div>

    <PopupEntry
      class="popup-entry-component"
      :show.sync="showPopupEntry"
      v-if="showPopupEntry"
      :title="popupEntryTitle"
      :updatedBy="updatedBy"
      :createdBy="createdBy"
      @save="popupEntrySaveClicked"
      :requiredError="popupEntryRequiredError"
      :successMessages="popupEntrySuccessMessages"
      :messages="popupEntryMessages"
      :errorMessages="popupEntryErrorMessages"
      @requiredClicked="popupEntryRequiredClicked"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
    >
      <div class="general-inputs">
        <div class="charge-code-and-active">
          <Input
            class="charge-code-input"
            placeholder="Charge Code"
            :inputValue.sync="chargeCodeInputValue"
            :isRequired="true"
            :isDisabled="viewModePK"
            :errorMessage.sync="chargeCodeErr"
            ref="chargeCodeErr"
            validator="masterfilecode,10,uppercase"
            @focusChanged="chargeCodeChange"
          />
          <CheckBoxV2
            v-if="SASShowActive"
            class="active-check-box"
            title="Active"
            :isChecked.sync="activeIsChecked"
            :isDisabled="viewMode"
          />
        </div>
        <Input
          placeholder="Description"
          :inputValue.sync="description"
          :isRequired="true"
          :errorMessage.sync="descriptionErr"
          :isDisabled="viewMode"
          validator="text,100,uppercase"
          ref="descriptionErr"
        />
      </div>

      <Tab
        class="tab-component"
        :tabs="tabItems"
        :tabIndex.sync="currentTab"
        @tabClicked="tabClicked"
        :preventTabSwitch="true"
      >
        <!-- Job Charge Code -->
        <div class="job-charge-code" slot="Job Charge Code">
          <div class="charge-type-radio-buttons">
            <div class="payable-and-billable">
              <RadioButtonSet
                class="radio-button-set-component"
                title="Payable"
                :buttons="payableRadioButtons"
                labelKey="label"
                :selected.sync="selectedPayable"
                :isRequired="true"
                :errorMessage.sync="payableErr"
                :isDisabled="viewMode"
                :isHorizontal="true"
                @radioChanged="payableRadioChanged"
                ref="payableErr"
              />
              <RadioButtonSet
                class="radio-button-set-component"
                title="Billable"
                :buttons="billableRadioButtons"
                labelKey="label"
                :selected.sync="selectedBillable"
                :isRequired="true"
                :errorMessage.sync="billableErr"
                :isDisabled="viewMode"
                :isHorizontal="true"
                @radioChanged="billableRadioChanged"
                ref="billableErr"
              />
            </div>
            <div class="ar-grouping-and-b2b">
              <RadioButtonSet
                class="radio-button-set-component"
                title="AR Grouping"
                :buttons="ARGroupingRadioButtons"
                labelKey="label"
                :selected.sync="selectedARGrouping"
                :isRequired="true"
                :errorMessage.sync="ARGroupingErr"
                :isDisabled="viewMode"
                :isHorizontal="true"
                ref="ARGroupingErr"
              />
              <RadioButtonSet
                class="radio-button-set-component"
                title="Is B2B Charge"
                :buttons="B2BChargeRadioButtons"
                labelKey="label"
                :selected.sync="selectedB2BCharge"
                :isRequired="true"
                :errorMessage.sync="B2BChargeErr"
                :isDisabled="viewMode"
                :isHorizontal="true"
                ref="B2BChargeErr"
              />
            </div>
          </div>
          <div class="charge-details-inputs">
            <div class="inputs-column">
              <InputQuery
                placeholder="Group Code"
                :isDisabled="viewMode"
                :items="chargeCodeListMaster"
                :allowFreeText="true"
                :selectedObject.sync="selectedGroupCode"
                :search.sync="selectedGroupCodeFreeText"
                :errorMessage.sync="selectedGroupCodeErr"
                itemKey="Charge Code"
                descKey="Description"
                ref="selectedGroupCodeErr"
                @focusChanged="chargeCodeChange"
              />
              <InputQuery
                placeholder="Unit Measurement"
                :isDisabled="viewMode"
                :items="UOMListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedUnitMeasurement"
                itemKey="UOM Code"
                descKey="Description"
              />
              <InputQuery
                placeholder="Code in Expenses"
                :items="codeInExpensesList"
                :isDisabled="viewMode"
                :allowFreeText="false"
                :selectedObject.sync="selectedCodeInExpenses"
                itemKey="Charge Code"
                descKey="Description"
              />
              <InputQuery
                placeholder="Status Code"
                :isDisabled="viewMode"
                :items="statusListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedStatusCode"
                itemKey="Status"
                descKey="Status Desc"
              />
              <InputQuery
                placeholder="Group Under"
                :isDisabled="viewMode"
                :items="chargeCodeGroupListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedGroupUnder"
                itemKey="Charges Group"
                descKey="Description"
              />
              <InputQuery
                placeholder="Classification"
                :isDisabled="viewMode"
                :items="chargeCodeClassListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedClassification"
                itemKey="Classification Code"
                descKey="Description"
              />
              <Input
                class="input-component"
                placeholder="SST Rate (%)"
                :inputValue.sync="SSTRateInputValue"
                :errorMessage.sync="SSTRateErr"
                :isDisabled="viewMode"
                validator="positiveInteger,3"
                ref="SSTRateErr"
              />
              <InputQuery
                placeholder="SST Service Type Code"
                :isDisabled="viewMode"
                :items="sstServiceTypeCodeListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedSSTCode"
                itemKey="Service Type Code"
                descKey="Service Type Desc"
              />
            </div>
            <div class="inputs-column">
              <InputQuery
                placeholder="AR GL Acc. Code"
                :items="GLAccListMaster"
                itemKey="Acc No"
                descKey="Description"
                :selectedObject.sync="selectedARGLAccCode"
                :isRequired="ARGLAccCodeIsRequired"
                :isDisabled="ARGLAccCodeIsDisabled"
                :allowFreeText="false"
                :errorMessage.sync="ARGLAccCodeErr"
                ref="ARGLAccCodeErr"
              />
              <InputQuery
                placeholder="GST/SST AR Tax Code"
                :items="ARTaxCodeListMaster"
                itemKey="Tax Code"
                descKey="Tax Code Desc"
                :selectedObject.sync="selectedGSTSSTARTaxCode"
                :isDisabled="viewMode"
                :allowFreeText="false"
              />
              <InputQuery
                placeholder="GST/SST AR GL Acc. Code"
                :items="GLAccListMaster"
                itemKey="Acc No"
                descKey="Description"
                :selectedObject.sync="selectedGSTSSTARGLAccCode"
                :isDisabled="viewMode"
                :allowFreeText="false"
              />
              <InputQuery
                placeholder="GST Major Industry Code"
                :items="MSICData"
                itemKey="MSIC Code"
                descKey="Description"
                :selectedObject.sync="selectedMSICCode"
                :isDisabled="viewMode"
                :allowFreeText="false"
              />
              <InputQuery
                placeholder="AP GL Acc. Code"
                :items="GLAccListMaster"
                itemKey="Acc No"
                descKey="Description"
                :selectedObject.sync="selectedAPGLAccCode"
                :errorMessage.sync="APGLAccCodeErr"
                :isRequired="APGLAccCodeIsRequired"
                :isDisabled="APGLAccCodeIsDisabled"
                :allowFreeText="false"
              />
              <InputQuery
                placeholder="GST AP Tax Code"
                :items="APTaxCodeListMaster"
                itemKey="Tax Code"
                descKey="Tax Code Desc"
                :selectedObject.sync="selectedGSTAPTaxCode"
                :isDisabled="viewMode"
                :allowFreeText="false"
                ref="APGLAccCodeErr"
              />
              <InputQuery
                placeholder="GST AP GL Acc. Code"
                :items="GLAccListMaster"
                itemKey="Acc No"
                descKey="Description"
                :selectedObject.sync="selectedGSTAPGLAccCode"
                :isDisabled="viewMode"
                :allowFreeText="false"
              />
            </div>
          </div>

          <div class="expenses-radio-button">
            <RadioButtonSet
              title="Expenses"
              :buttons="expensesRadioButtons"
              labelKey="label"
              :selected.sync="selectedExpenses"
              :isRequired="expensesIsRequired"
              :errorMessage.sync="expensesErr"
              :isDisabled="expensesIsDisabled"
              :isHorizontal="true"
              ref="expensesErr"
            />
          </div>
        </div>

        <!-- Transport Charges To GL Code -->
        <div class="transport-charge" slot="Transport Charge To GL Code">
          <div class="transport-charge-list">
            <div v-if="!viewMode" class="call-to-action-buttons">
              <button @click="addTransportChargeButtonClicked" :disabled="showTransportChargeInputs">Add</button>
              <button @click="deleteTransportChargeButtonClicked" :disabled="showTransportChargeInputs">Delete</button>
            </div>
            <div class="table-box-container">
              <TableBox
                v-if="showTransportChargeTableBox"
                title="Transport Charge"
                :tableData="transportChargeTableDataWithoutDeleted"
                :selectedRow.sync="transportChargeTableBoxSelectedRow"
                :hideColumns="transportChargeTableBoxHideColumns"
                :additionalButtons="transportChargeTableBoxAdditionalButtons"
                @buttonClicked="editTransportChargeButtonClicked"
              />
            </div>
          </div>
          <div v-if="showTransportChargeInputs" class="transport-charge-inputs">
            <div class="call-to-action-buttons">
              <button v-if="transportChargeBeingEdited === null" @click="addTransportChargeInputButtonClicked"
                >Add</button
              >
              <button v-else @click="editTransportChargeInputButtonClicked">Edit</button>
              <button @click="cancelTransportChargeInputButtonClicked">Cancel</button>
            </div>
            <div class="inputs">
              <InputQuery
                placeholder="GL Acc. Code"
                :isRequired="true"
                :isDisabled="viewMode"
                :items="GLAccListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedTransportChargeGLAccCode"
                itemKey="Acc No"
                descKey="Description"
                :errorMessage.sync="transportChargeGLAccCodeErr"
              />
              <InputQuery
                placeholder="Default GL Acc. Code"
                :isRequired="true"
                :isDisabled="viewMode"
                :items="GLAccListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedDefaultGLAccCode"
                itemKey="Acc No"
                descKey="Description"
                :errorMessage.sync="defaultGLAccCodeErr"
              />
              <InputQuery
                placeholder="Income Acc. Code"
                :isDisabled="viewMode"
                :items="GLAccListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedIncAccCode"
                itemKey="Acc No"
                descKey="Description"
              />
              <InputQuery
                placeholder="Vehicle No"
                :isDisabled="viewMode"
                :items="lorryNoListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedVehicleNo"
                itemKey="Lorry No"
                descKey="Vehicle Code"
              />
              <InputQuery
                placeholder="Txn Type"
                :isDisabled="viewMode"
                :items="GLTxnTypeListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedTxnType"
                itemKey="GL Txn Type"
              />
              <InputQuery
                placeholder="Job Type"
                :isDisabled="viewMode"
                :items="jobTypeListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedJobType"
                itemKey="JobType"
                descKey="JobTypeDesc"
                @selected="jobTypeSelected"
              />
              <InputQuery
                placeholder="Zone Type"
                :isDisabled="viewMode"
                :items="zoneListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedZoneType"
                itemKey="Zone Type"
                descKey="Description"
              />
              <InputQuery
                placeholder="Haulier Code"
                :isDisabled="viewMode"
                :items="haulierListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedHaulierCode"
                itemKey="Haulier Code"
                descKey="Description"
              />
              <InputQuery
                placeholder="Container Size"
                :isDisabled="viewMode"
                :items="contSizeListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedContSize"
                itemKey="Size"
                descKey="Size"
              />
              <InputQuery
                placeholder="Delivery Type"
                :isDisabled="viewMode"
                :items="contDeliveryTypeListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedDeliveryType"
                itemKey="Delivery Type"
                descKey="Description"
              />
              <InputQuery
                placeholder="Employee Branch"
                :isDisabled="viewMode"
                :items="branchListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedEmployeeBranch"
                itemKey="Branch Code"
                descKey="Branch Name"
              />
              <InputQuery
                placeholder="Employee Department"
                :isDisabled="viewMode"
                :items="departmentListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedEmployeeDepartment"
                itemKey="ID"
                descKey="DeptDesc"
              />
              <InputQuery
                placeholder="Job System"
                :isDisabled="viewMode"
                :items="jobSystemListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedJobSystem"
                itemKey="System"
                descKey="Application Name"
              />
              <InputQuery
                placeholder="Transport Mode"
                :isDisabled="viewMode"
                :items="transportModeListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedTransMode"
                itemKey="Trans Mode"
                descKey="Description"
              />
            </div>
          </div>
        </div>

        <!-- EMS Charges To GL Code -->
        <div class="ems-charge" slot="EMS Charge To GL Code">
          <div class="ems-charge-list">
            <div v-if="!viewMode" class="call-to-action-buttons">
              <button @click="addEMSChargeButtonClicked" :disabled="showEMSChargeInputs">Add</button>
              <button @click="deleteEMSChargeButtonClicked" :disabled="showEMSChargeInputs">Delete</button>
            </div>
            <div class="table-box-container">
              <TableBox
                v-if="showEMSChargeTableBox"
                title="EMS Charge"
                :tableData="EMSChargeTableDataWithoutDeleted"
                :selectedRow.sync="EMSChargeTableBoxSelectedRow"
                :hideColumns="EMSChargeTableBoxHideColumns"
                :additionalButtons="EMSChargeTableBoxAdditionalButtons"
                @buttonClicked="editEMSChargeButtonClicked"
              />
            </div>
          </div>
          <div v-if="showEMSChargeInputs" class="ems-charge-input">
            <div class="call-to-action-buttons">
              <button v-if="EMSChargeBeingEdited === null" @click="addEMSChargeInputButtonClicked">Add</button>
              <button v-else @click="editEMSChargeInputButtonClicked">Edit</button>
              <button @click="cancelEMSChargeInputButtonClicked">Cancel</button>
            </div>
            <div class="inputs">
              <InputQuery
                placeholder="GL Acc. Code"
                :isRequired="true"
                :isDisabled="viewMode"
                :items="GLAccListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedEMSChargeGLAccCode"
                itemKey="Acc No"
                descKey="Description"
                :errorMessage.sync="EMSChargeGLAccCodeErr"
              />
              <InputQuery
                placeholder="Vehicle Code"
                :isRequired="true"
                :isDisabled="viewMode"
                :items="lorryNoListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedVehicleCode"
                itemKey="Vehicle Code"
                descKey="Lorry No"
                :errorMessage.sync="vehicleCodeErr"
              />
              <Input
                class="input-component"
                placeholder="Ratio"
                :inputValue.sync="ratioInputValue"
                :isRequired="true"
                :isDisabled="viewMode"
                :errorMessage.sync="ratioErr"
                validator="positiveDecimal,3,4"
              />
              <InputQuery
                placeholder="Part Type"
                :isDisabled="viewMode"
                :items="EMSPartTypeListMaster"
                :allowFreeText="false"
                :selectedObject.sync="selectedPartType"
                itemKey="Part Type"
                descKey="Description"
              />
            </div>
          </div>
        </div>
      </Tab>
    </PopupEntry>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    ARGLAccCodeIsRequired: function () {
      return this.selectedBillable?.label === 'Yes' || !this.selectedBillable; // selectedBillable should never be null, because nowhere in the code is selectedBillable ever cleared
    },
    ARGLAccCodeIsDisabled: function () {
      return this.selectedBillable?.label === 'No' || this.viewMode || !this.selectedBillable; // selectedBillable should never be null, because nowhere in the code is selectedBillable ever cleared
    },
    APGLAccCodeIsRequired: function () {
      return this.selectedPayable?.label === 'Yes' || !this.selectedPayable; // selectedPayable should never be null, because nowhere in the code is selectedPayable ever cleared
    },
    APGLAccCodeIsDisabled: function () {
      return this.selectedPayable?.label === 'No' || this.viewMode || !this.selectedPayable; // selectedPayable should never be null, because nowhere in the code is selectedPayable ever cleared
    },
    codeInExpensesList: function () {
      if (!this.chargeCodeListMaster) {
        return [];
      }

      return this.chargeCodeListMaster.filter((chargeCode) => {
        return chargeCode.Payable === 'Both' || chargeCode.Payable === 'Expenses';
      });
    },
    expensesIsRequired: function () {
      return this.selectedPayable?.label === 'Yes' || !this.selectedPayable; // selectedPayable should never be null, because nowhere in the code is selectedPayable ever cleared
    },
    expensesIsDisabled: function () {
      return this.selectedPayable?.label === 'No' || this.viewMode || !this.selectedPayable; // selectedPayable should never be null, because nowhere in the code is selectedPayable ever cleared
    },
    transportChargeTableDataWithoutDeleted: function () {
      // return transport charges that are not deleted
      return this.transportChargeTableData.filter((transportCharge) => transportCharge._action !== 'D');
    },
    transportChargeTableBoxAdditionalButtons: function () {
      // hide additional buttons when the PopupEntry is in view mode, need to hide and re-show the TableBox for it to take effect
      return !this.viewMode ? [{ text: 'Edit', index: 0, width: 75 }] : [];
    },
    EMSChargeTableDataWithoutDeleted: function () {
      // return EMS charges that are not deleted
      return this.EMSChargeTableData.filter((EMSCharge) => EMSCharge._action !== 'D');
    },
    EMSChargeTableBoxAdditionalButtons: function () {
      // hide additional buttons when the PopupEntry is in view mode, need to hide and re-show the TableBox for it to take effect
      return !this.viewMode ? [{ text: 'Edit', index: 0, width: 75 }] : [];
    },
  },
  data: function () {
    return {
      // Used to store all responses from GetListMaster, some of them may be shared between multiple Dropdown or InputQuery
      chargeCodeListMaster: [],
      UOMListMaster: [],
      statusListMaster: [],
      chargeCodeGroupListMaster: [],
      chargeCodeClassListMaster: [],
      GLAccListMaster: [],
      ARTaxCodeListMaster: [],
      APTaxCodeListMaster: [],
      GLTxnTypeListMaster: [],
      lorryNoListMaster: [],
      EMSPartTypeListMaster: [],
      jobTypeListMaster: [],
      zoneListMaster: [],
      haulierListMaster: [],
      contSizeListMaster: [],
      contDeliveryTypeListMaster: [],
      branchListMaster: [],
      departmentListMaster: [],
      sstServiceTypeCodeListMaster: [],
      jobSystemListMaster: [],
      transportModeListMaster: [],

      menu: [
        {
          title: 'New Charge Code',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'MnuNew',
          visible: true,
          enabled: false,
        },
        {
          title: 'Edit Charge Code',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          key: 'MnuEdit',
          visible: true,
          enabled: false,
        },
        {
          title: 'Delete Charge Code',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'MnuDelete',
          visible: false,
          enabled: false,
        }, //api response logic abit weird ("Hide Delete" instead of "Delete")
        {
          title: 'View Charge Code',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'MnuQuery',
          visible: true,
          enabled: false,
        },
      ],

      chargeCodeTableBoxSelectedRow: [],
      chargeCodeTableBoxLoading: false,

      // popup entry state
      showPopupEntry: false,
      updatedBy: '',
      createdBy: '',
      viewMode: false,
      viewModePK: false,
      popupEntryRequiredError: [],
      popupEntrySuccessMessages: [],
      popupEntryMessages: [],
      popupEntryErrorMessages: [],

      // popup entry common fields
      chargeCodeInputValue: '',
      chargeCodeErr: '',
      SASShowActive: false, // same logic as Delete Charge Code menu button, SAS option is "Hide Active" instead of "Active"
      activeIsChecked: false,
      description: '',
      descriptionErr: '',

      // tab state
      tabItems: [
        { title: 'Job Charge Code', disabled: false },
        { title: 'Transport Charge To GL Code', disabled: false, key: 'TptChgToGL' },
        { title: 'EMS Charge To GL Code', disabled: false, key: 'EmsChgToGL' },
      ],
      currentTab: 0,
      errorTabMapping: [
        // technically not needed, since all required errors displayed in PopupEntry are for the first tab, but leaving it here in case it's needed in the future
        { key: 'chargeCodeErr', tab: 0 },
        { key: 'descriptionErr', tab: 0 },
        { key: 'payableErr', tab: 0 },
        { key: 'billableErr', tab: 0 },
        { key: 'ARGroupingErr', tab: 0 },
        { key: 'selectedGroupCodeErr', tab: 0 },
        { key: 'B2BChargeErr', tab: 0 },
        { key: 'SSTRateErr', tab: 0 },
        { key: 'ARGLAccCodeErr', tab: 0 },
        { key: 'GSTMajorIndustryCodeErr', tab: 0 },
        { key: 'APGLAccCodeErr', tab: 0 },
        { key: 'expensesErr', tab: 0 },
      ],

      // job charge code tab
      payableRadioButtons: [
        { label: 'Yes', keyValue: '1' },
        { label: 'No', keyValue: '0' },
      ],
      selectedPayable: null,
      payableErr: '',

      billableRadioButtons: [
        { label: 'Yes', keyValue: '1' },
        { label: 'No', keyValue: '0' },
      ],
      selectedBillable: null,
      billableErr: '',

      ARGroupingRadioButtons: [
        { label: 'Yes', keyValue: 'Y' },
        { label: 'No', keyValue: 'N' },
      ],
      selectedARGrouping: null,
      ARGroupingErr: '',

      B2BChargeRadioButtons: [
        { label: 'Yes', keyValue: '1' },
        { label: 'No', keyValue: '0' },
      ],
      selectedB2BCharge: null,
      B2BChargeErr: '',

      selectedGroupCode: null,
      selectedGroupCodeFreeText: '',
      selectedGroupCodeErr: '',
      selectedUnitMeasurement: null,
      selectedCodeInExpenses: null,
      selectedStatusCode: null,
      selectedGroupUnder: null,
      selectedClassification: null,
      SSTRateInputValue: '',
      SSTRateErr: '',

      selectedARGLAccCode: null,
      ARGLAccCodeErr: '',
      selectedGSTSSTARTaxCode: null,
      selectedGSTSSTARGLAccCode: null,
      MSICData: [],
      selectedMSICCode: null,
      GSTMajorIndustryCodeErr: '',
      selectedAPGLAccCode: null,
      APGLAccCodeErr: '',
      selectedGSTAPTaxCode: null,
      selectedGSTAPGLAccCode: null,

      expensesRadioButtons: [
        { label: 'No Constraint', keyValue: '0' },
        { label: 'Use In Expenses Only', keyValue: '1' },
        { label: 'Expenses & Select Expenses & Posting', keyValue: '2' },
        { label: 'Expenses & Select Expenses & No Posting', keyValue: '3' },
      ],
      selectedExpenses: null,
      expensesErr: '',

      // Transport Charge To GL Code
      showTransportChargeTableBox: true, // used to force a re-execution of TableBox's mounted, in order to update _rowid
      transportChargeTableData: [],
      transportChargeTableBoxSelectedRow: [],
      transportChargeTableBoxHideColumns: ['Charge Code', 'Seq', 'CoCode', '_action'],

      transportChargeBeingEdited: null, // stores the transport charge the user is currently editting, used to update the row that was edited once user confirms inputs
      // compare with SeqNo if available, if not then with _rowid

      showTransportChargeInputs: false,
      selectedTransportChargeGLAccCode: null,
      transportChargeGLAccCodeErr: '',
      selectedDefaultGLAccCode: null,
      defaultGLAccCodeErr: '',
      selectedIncAccCode: null,
      selectedVehicleNo: null,
      selectedTxnType: null,
      selectedJobType: null,
      selectedZoneType: null,
      selectedHaulierCode: null,
      selectedContSize: null,
      selectedDeliveryType: null,
      selectedEmployeeBranch: null,
      selectedEmployeeDepartment: null,
      selectedSSTCode: null,
      selectedJobSystem: null,
      selectedTransMode: null,

      // EMS Charge To GL Code
      showEMSChargeTableBox: true, // used to force a re-execution of TableBox's mounted, in order to update _rowid
      EMSChargeTableData: [],
      EMSChargeTableBoxSelectedRow: [],
      EMSChargeTableBoxHideColumns: ['Charge Code', 'CoCode', 'Seq', '_action'],

      EMSChargeBeingEdited: null, // stores the EMS charge the user is currently editting, used to update the row that was edited once user confirms inputs
      // compare with SeqNo if available, if not then with _rowid

      showEMSChargeInputs: false,
      selectedEMSChargeGLAccCode: null,
      EMSChargeGLAccCodeErr: '',
      selectedVehicleCode: null,
      vehicleCodeErr: '',
      ratioInputValue: '',
      ratioErr: '',
      selectedPartType: null,
    };
  },
  props: {},
  methods: {
    getChargeCodeListMaster: async function () {
      this.chargeCodeTableBoxLoading = true; // this GetListMaster is used by the main tablebox and some other Dropdowns/InputQueries
      this.chargeCodeListMaster = [];
      this.chargeCodeTableBoxSelectedRow = [];
      this.chargeCodeListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CHARGESCODE' });
      this.chargeCodeTableBoxLoading = false;
    },

    getChargeCodeDetails: async function (chargeCode) {
      return (
        await this.$apiCallV2('/Master/GetDetailsMaster', {
          masterfile: 'CHARGECODE',
          module: 'MASTER',
          recordid: chargeCode,
        })
      )['ChargeCode'];
    },

    menuClicked: async function (e) {
      this.clearPopupEntryMessages();
      this.resetPopupEntryFields();

      switch (e.title) {
        case 'New Charge Code':
          this.popupEntryTitle = 'New Charge Code';
          this.viewMode = false;
          this.viewModePK = false;
          this.showPopupEntry = true;
          break;

        case 'Edit Charge Code':
          if (this.chargeCodeTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No Charge Code is selected', 'Please select a Charge Code to edit');
            break;
          }

          this.$fullscreenLoading('Getting Charge Code Details');
          this.populatePopupEntryFields(
            await this.getChargeCodeDetails(this.chargeCodeTableBoxSelectedRow[0]['Charge Code']),
          );
          this.$fullscreenLoading();

          this.viewMode = false;
          this.viewModePK = true;
          this.showPopupEntry = true;
          this.popupEntryTitle = 'Edit Charge Code';
          break;

        case 'Delete Charge Code': {
          if (this.chargeCodeTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No charge code is selected', 'Please select a charge code to delete');
            break;
          }

          this.$fullscreenLoading('Getting Charge Code Details');
          this.populatePopupEntryFields(
            await this.getChargeCodeDetails(this.chargeCodeTableBoxSelectedRow[0]['Charge Code']),
          );
          this.$fullscreenLoading();

          let promptMessage = `Are you sure you want to delete Charge Code <b>${this.chargeCodeTableBoxSelectedRow[0]['Charge Code']}</b>?`;
          const additionalDeletions = [];
          if (this.transportChargeTableData.length > 0) {
            additionalDeletions.push(
              `<b>${this.transportChargeTableData.length} transport charge${this.transportChargeTableData.length > 1 ? 's' : ''}</b>`,
            );
          }
          if (this.EMSChargeTableData.length > 0) {
            additionalDeletions.push(
              `<b>${this.EMSChargeTableData.length} EMS charge${this.EMSChargeTableData.length > 1 ? 's' : ''}</b>`,
            );
          }
          if (additionalDeletions.length > 0) {
            promptMessage += `<br><br>This will also delete ${additionalDeletions.join(' and ')}`;
          }

          const deleteConfirmation = await this.$showAlert('warning', 'Delete Charge Code', promptMessage, [
            { text: 'Delete', textColor: 'white', color: 'red' },
            { text: 'Cancel' },
          ]);
          if (deleteConfirmation == 'Cancel') {
            break;
          }

          this.$fullscreenLoading('Deleting Charge Code');
          const result = await this.$apiCallV2('/ChargeCode/SaveChargeCode', this.formatSaveChargeCodeRequestBody('D'));
          this.$fullscreenLoading();

          if (result[0]['Results'] != '0') {
            // Delete error
            this.$showAlert(
              'error',
              'Charge Code NOT Deleted',
              `There was an error deleting Charge Code ${this.chargeCodeTableBoxSelectedRow[0]['Charge Code']}`,
            );
            break;
          }

          // Delete successful
          this.$showAlert(
            'success',
            'Charge Code Deleted',
            `Successfully deleted Charge Code ${this.chargeCodeTableBoxSelectedRow[0]['Charge Code']}`,
          );
          this.chargeCodeTableBoxSelectedRow = [];
          this.getChargeCodeListMaster();
          break;
        }

        case 'View Charge Code':
          if (this.chargeCodeTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No Charge Code is selected', 'Please select a Charge Code to view');
            break;
          }

          this.$fullscreenLoading('Getting Charge Code Details');
          this.populatePopupEntryFields(
            await this.getChargeCodeDetails(this.chargeCodeTableBoxSelectedRow[0]['Charge Code']),
          );
          this.$fullscreenLoading();

          this.popupEntryTitle = 'View Charge Code';
          this.viewMode = true;
          this.viewModePK = true;
          this.showPopupEntry = true;
          break;
      }
    },

    clearPopupEntryMessages: function () {
      this.popupEntryRequiredError = [];
      this.popupEntrySuccessMessages = [];
      this.popupEntryMessages = [];
      this.popupEntryErrorMessages = [];
    },
    resetPopupEntryFields: function () {
      // clears off most fields, and set defaults for some
      // popup entry state
      this.popupEntryTitle = '';
      this.createdBy = '';
      this.updatedBy = '';

      // common fields
      this.chargeCodeInputValue = '';
      this.chargeCodeErr = '';
      this.activeIsChecked = true;
      this.description = '';
      this.descriptionErr = '';

      // tab state
      this.currentTab = 0;

      // job charge code fields
      this.selectedPayable = this.populateObject(this.payableRadioButtons, 'label', 'Yes');
      this.payableErr = '';
      this.selectedBillable = this.populateObject(this.billableRadioButtons, 'label', 'Yes');
      this.billableErr = '';
      this.selectedARGrouping = this.populateObject(this.ARGroupingRadioButtons, 'label', 'Yes');
      this.ARGroupingErr = '';
      this.selectedB2BCharge = this.populateObject(this.B2BChargeRadioButtons, 'label', 'No');
      this.B2BChargeErr = '';

      this.selectedGroupCode = null;
      this.selectedGroupCodeFreeText = '';
      this.selectedGroupCodeErr = '';
      this.selectedUnitMeasurement = null;
      this.selectedCodeInExpenses = null;
      this.selectedStatusCode = null;
      this.selectedGroupUnder = null;
      this.selectedClassification = null;
      this.SSTRateInputValue = '';
      this.SSTRateErr = '';
      this.selectedSSTCode = null;

      this.selectedARGLAccCode = null;
      this.ARGLAccCodeErr = '';
      this.selectedGSTSSTARTaxCode = null;
      this.selectedGSTSSTARGLAccCode = null;
      this.selectedMSICCode = null;
      this.GSTMajorIndustryCodeErr = '';
      this.selectedAPGLAccCode = null;
      this.APGLAccCodeErr = '';
      this.selectedGSTAPTaxCode = null;
      this.selectedGSTAPGLAccCode = null;
      this.selectedExpenses = this.expensesRadioButtons.find((radioButton) => radioButton.label === 'No Constraint');
      this.expensesErr = '';

      // Transport Charge To GL Code
      this.transportChargeTableData = [];
      this.showTransportChargeInputs = false;
      // does not clear input fields for transport charge, there's a separate function for that

      // EMS Charge To GL Code
      this.EMSChargeTableData = [];
      this.showEMSChargeInputs = false;
      // does not clear input fields for EMS charge, there's a separate function for that
    },
    populatePopupEntryFields: function (chargeCodeDetails) {
      // popup entry state
      this.createdBy = `${chargeCodeDetails['Created By']?.trim() ?? ''} ${chargeCodeDetails['Created On'] ? new Date(chargeCodeDetails['Created On']).formatString('dd/MM/yyyy HH:mm') : ''}`;
      this.updatedBy = `${chargeCodeDetails['UserID']?.trim() ?? ''} ${chargeCodeDetails['DateUpdate'] ? new Date(chargeCodeDetails['DateUpdate']).formatString('dd/MM/yyyy HH:mm') : ''}`;

      // common fields
      this.chargeCodeInputValue = this.populateTextTrim(chargeCodeDetails['Charge Code']);
      this.activeIsChecked = chargeCodeDetails['Active'] == '1';
      this.description = this.populateTextTrim(chargeCodeDetails['Description']);

      // Job Charge Code tab
      switch (chargeCodeDetails['Payable']) {
        case '0': // not payable but billable
          this.selectedPayable = this.populateObject(this.payableRadioButtons, 'label', 'No');
          this.selectedBillable = this.populateObject(this.billableRadioButtons, 'label', 'Yes');
          break;

        case '1': // payable and billable
          this.selectedPayable = this.populateObject(this.payableRadioButtons, 'label', 'Yes');
          this.selectedBillable = this.populateObject(this.billableRadioButtons, 'label', 'Yes');
          break;

        case '2': // payable but not billable
          this.selectedPayable = this.populateObject(this.payableRadioButtons, 'label', 'Yes');
          this.selectedBillable = this.populateObject(this.billableRadioButtons, 'label', 'No');
          break;

        case '3': // not payable and not billable
          this.selectedPayable = this.populateObject(this.payableRadioButtons, 'label', 'No');
          this.selectedBillable = this.populateObject(this.billableRadioButtons, 'label', 'No');
          break;
      }
      this.selectedARGrouping = this.populateObject(
        this.ARGroupingRadioButtons,
        'keyValue',
        chargeCodeDetails['RptGroup'],
      );
      this.selectedB2BCharge = this.populateObject(this.B2BChargeRadioButtons, 'keyValue', chargeCodeDetails['B2B']);

      this.selectedGroupCode =
        chargeCodeDetails['Group Code'] ?
          this.populateObject(this.chargeCodeListMaster, 'Charge Code', chargeCodeDetails['Group Code'])
        : null;
      this.selectedUnitMeasurement =
        chargeCodeDetails['UOM'] ? this.populateObject(this.UOMListMaster, 'UOM Code', chargeCodeDetails['UOM']) : null;
      this.selectedCodeInExpenses =
        chargeCodeDetails['ExpCode'] ?
          this.populateObject(this.codeInExpensesList, 'Charge Code', chargeCodeDetails['ExpCode'])
        : null;
      this.selectedStatusCode =
        chargeCodeDetails['Status Code'] ?
          this.populateObject(this.statusListMaster, 'Status', chargeCodeDetails['Status Code'])
        : null;
      this.selectedGroupUnder =
        chargeCodeDetails['Charge Group'] ?
          this.populateObject(this.chargeCodeGroupListMaster, 'Charges Group', chargeCodeDetails['Charge Group'])
        : null;
      this.selectedClassification =
        chargeCodeDetails['Classification'] ?
          this.populateObject(
            this.chargeCodeClassListMaster,
            'Classification Code',
            chargeCodeDetails['Classification'],
          )
        : null;
      this.SSTRateInputValue = this.populateTextTrim(chargeCodeDetails['Tax Rate']);
      this.selectedSSTCode =
        chargeCodeDetails['SSTServiceTypeCode'] ?
          this.populateObject(
            this.sstServiceTypeCodeListMaster,
            'Service Type Code',
            chargeCodeDetails['SSTServiceTypeCode'],
          )
        : null;

      this.selectedARGLAccCode =
        chargeCodeDetails['AR Acc. Code'] ?
          this.populateObject(this.GLAccListMaster, 'Acc No', chargeCodeDetails['AR Acc. Code'])
        : null;
      this.selectedGSTSSTARTaxCode =
        chargeCodeDetails['ARGST2TaxCode'] ?
          this.populateObject(this.ARTaxCodeListMaster, 'Tax Code', chargeCodeDetails['ARGST2TaxCode'])
        : null;
      this.selectedGSTSSTARGLAccCode =
        chargeCodeDetails['ARGST2GLAcc'] ?
          this.populateObject(this.GLAccListMaster, 'Acc No', chargeCodeDetails['ARGST2GLAcc'])
        : null;
      this.selectedMSICCode =
        chargeCodeDetails['GST2MSICCode'] ?
          this.populateObject(this.MSICData, 'MSIC Code', chargeCodeDetails['GST2MSICCode'])
        : null;
      this.selectedAPGLAccCode =
        chargeCodeDetails['AP Acc. Code'] ?
          this.populateObject(this.GLAccListMaster, 'Acc No', chargeCodeDetails['AP Acc. Code'])
        : null;
      this.selectedGSTAPTaxCode =
        chargeCodeDetails['APGST2TaxCode'] ?
          this.populateObject(this.APTaxCodeListMaster, 'Tax Code', chargeCodeDetails['APGST2TaxCode'])
        : null;
      this.selectedGSTAPGLAccCode =
        chargeCodeDetails['APGST2GLAcc'] ?
          this.populateObject(this.GLAccListMaster, 'Acc No', chargeCodeDetails['APGST2GLAcc'])
        : null;

      this.selectedExpenses = this.populateObject(this.expensesRadioButtons, 'keyValue', chargeCodeDetails['Exp Type']);

      // Transport Charge To GL Code tab
      this.transportChargeTableData = chargeCodeDetails['TransportChrgGL'] ?? [];
      // does not populate input fields for transport charge, there's a separate function for that

      // EMS Charge To GL Code tab
      this.EMSChargeTableData = chargeCodeDetails['EMSChrgGL'] ?? [];
      // does not populate input fields for EMS charge, there's a separate function for that
    },

    payableRadioChanged: function (selectedRadioButton) {
      if (selectedRadioButton?.label === 'No') {
        this.selectedAPGLAccCode = null;
        this.APGLAccCodeErr = '';
        this.selectedExpenses = null;
        this.expensesErr = '';
        return;
      }

      // else if label === "Yes"
      this.selectedExpenses = this.expensesRadioButtons.find((radioButton) => radioButton.label === 'No Constraint');
      this.expensesErr = '';
    },
    billableRadioChanged: function (selectedRadioButton) {
      if (selectedRadioButton?.label === 'No') {
        this.selectedARGLAccCode = null;
        this.ARGLAccCodeErr = '';
      }
    },

    addTransportChargeButtonClicked: function (_pointerEvent) {
      this.clearTransportChargeInputs();
      this.showTransportChargeInputs = true;
    },
    editTransportChargeButtonClicked: function (_buttonText, rowClicked) {
      this.transportChargeBeingEdited = {
        ...rowClicked,
      };
      this.clearTransportChargeInputs();
      this.populateTransportChargeInputs(rowClicked);
      this.showTransportChargeInputs = true;
    },
    deleteTransportChargeButtonClicked: async function (_pointerEvent) {
      if (this.transportChargeTableBoxSelectedRow.length === 0) {
        this.$showAlert('error', 'No Transport Charge Selected', 'Please select a transport charge to delete', [
          { text: 'OK' },
        ]);
        return;
      }

      const deleteConfirmation = await this.$showAlert(
        'warning',
        'Delete Transport Charge',
        'Are you sure you wish to delete the selected transport charge?',
        [{ text: 'Delete', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
      if (deleteConfirmation === 'Cancel') {
        return;
      }

      const transportChargeToDelete = this.transportChargeTableBoxSelectedRow[0];
      let matchedIndex; // find the row being deleted in the tablebox data
      if (transportChargeToDelete.Seq !== undefined && transportChargeToDelete.Seq !== null) {
        // if Seq exists, strict comparison with undefined and null because Seq might be 0
        const foundIndex = this.transportChargeTableData.findIndex(
          (transportCharge) => transportCharge.Seq === transportChargeToDelete.Seq,
        );
        if (foundIndex !== -1) {
          // if a match was found with Seq
          matchedIndex = foundIndex; // assign the matching index
        }
      }
      if (matchedIndex === undefined && transportChargeToDelete._rowid) {
        // if Seq does not exist or could not find a match with Seq, and _rowid exists
        const foundIndex = this.transportChargeTableData.findIndex(
          (transportCharge) => transportCharge._rowid === transportChargeToDelete._rowid,
        ); // try matching with _rowid instead
        if (foundIndex !== -1) {
          // if a match was found with _rowid
          matchedIndex = foundIndex; // assign the matching index
        }
      }
      if (matchedIndex === undefined) {
        // if a match still can't be found, which shouldn't happen
        throw new Error('ChargeCode - Could not find the row being deleted');
      }

      if (transportChargeToDelete._action === 'N') {
        // if deleting a newly added transport charge, simply remove the element from the array
        this.transportChargeTableData.splice(matchedIndex, 1);
      } else {
        // if deleting an existing transport charge, set the _action to "D" and POST to API
        this.transportChargeTableData[matchedIndex]._action = 'D';
      }

      const modifiedTransportChargeTableData = this.transportChargeTableData.map((transportCharge) => {
        delete transportCharge._rowid;
        return transportCharge;
      });
      this.showTransportChargeTableBox = false; // hide TableBox and re-render on nextTick
      this.transportChargeTableData = [];
      this.transportChargeTableBoxSelectedRow = [];
      this.$nextTick(() => {
        this.transportChargeTableData = modifiedTransportChargeTableData;
        this.showTransportChargeTableBox = true; // re-render to force a re-execution of mounted in TableBox, in order to update _rowid
      });
    },
    clearTransportChargeInputs: function () {
      this.selectedTransportChargeGLAccCode = null;
      this.transportChargeGLAccCodeErr = '';
      this.selectedDefaultGLAccCode = null;
      this.defaultGLAccCodeErr = '';
      this.selectedIncAccCode = null;
      this.selectedVehicleNo = null;
      this.selectedTxnType = null;
      this.selectedJobType = null;
      this.selectedZoneType = null;
      this.selectedHaulierCode = null;
      this.selectedContSize = null;
      this.selectedDeliveryType = null;
      this.selectedEmployeeBranch = null;
      this.selectedEmployeeDepartment = null;
      this.selectedJobSystem = null;
      this.selectedTransMode = null;
    },
    populateTransportChargeInputs: function (transportChargeDetails) {
      this.selectedTransportChargeGLAccCode = this.populateObject(
        this.GLAccListMaster,
        'Acc No',
        transportChargeDetails['GL Acc. Code'],
      );
      this.selectedDefaultGLAccCode = this.populateObject(
        this.GLAccListMaster,
        'Acc No',
        transportChargeDetails['Default GL Acc. Code'],
      );
      this.selectedVehicleNo = this.populateObject(
        this.lorryNoListMaster,
        'Lorry No',
        transportChargeDetails['Vehicle No'],
      );

      let fullTxnType;
      switch (transportChargeDetails['Txn']) {
        case 'E':
          fullTxnType = 'EXPENSES';
          break;

        case 'I':
          fullTxnType = 'INCOME';
          break;
      }
      this.selectedTxnType = this.populateObject(this.GLTxnTypeListMaster, 'GL Txn Type', fullTxnType);
      this.selectedIncAccCode = this.populateObject(
        this.GLAccListMaster,
        'Acc No',
        transportChargeDetails['Income Acc. Code'],
      );
      this.selectedJobType = this.populateObject(this.jobTypeListMaster, 'JobType', transportChargeDetails['Job Type']);
      this.selectedZoneType = this.populateObject(
        this.zoneListMaster,
        'Zone Type',
        transportChargeDetails['Zone Type'],
      );
      this.selectedHaulierCode = this.populateObject(
        this.haulierListMaster,
        'Haulier Code',
        transportChargeDetails['Haulier Code'],
      );
      this.selectedContSize = this.populateObject(
        this.contSizeListMaster,
        'Size',
        transportChargeDetails['Cont. Size'],
      );
      this.selectedDeliveryType = this.populateObject(
        this.contDeliveryTypeListMaster,
        'Delivery Type',
        transportChargeDetails['Delivery Type'],
      );
      this.selectedEmployeeBranch = this.populateObject(
        this.branchListMaster,
        'Branch Code',
        transportChargeDetails['Employee Branch'],
      );
      this.selectedEmployeeDepartment = this.populateObject(
        this.departmentListMaster,
        'ID',
        transportChargeDetails['Employee Department'],
      );
      this.selectedJobSystem = this.populateObject(
        this.jobSystemListMaster,
        'System',
        transportChargeDetails['Job System'],
      );
      this.selectedTransMode = this.populateObject(
        this.transportModeListMaster,
        'Trans Mode',
        transportChargeDetails['Transport Mode'],
      );
    },

    addTransportChargeInputButtonClicked: function (_pointerEvent) {
      const isInvalid = this.checkTransportChargeFields();
      if (isInvalid) {
        return;
      }

      const modifiedTransportChargeTableData = [
        ...this.transportChargeTableData.map((transportCharge) => {
          delete transportCharge._rowid;
          return transportCharge;
        }),
        {
          // populate "Charge Code" later when actually saving instead, use input value from common field
          // this way, won't have to prevent the user from entering charge code first before creating a new transport charge

          'GL Acc. Code': this.selectedTransportChargeGLAccCode['Acc No'],
          'Delivery Type': this.selectedDeliveryType?.['Delivery Type'] ?? '',
          'Zone Type': this.selectedZoneType?.['Zone Type'] ?? '',
          'Vehicle No': this.selectedVehicleNo?.['Lorry No'] ?? '',
          Txn: this.selectedTxnType?.['GL Txn Type']?.substring?.(0, 1) ?? '',
          'Default GL Acc. Code': this.selectedDefaultGLAccCode['Acc No'],
          'Income Acc. Code': this.selectedIncAccCode ? this.selectedIncAccCode['Acc No'] : '',
          'Haulier Code': this.selectedHaulierCode?.['Haulier Code'] ?? '',
          'Job Type': this.selectedJobType?.JobType ?? '',
          'Cont. Size': this.selectedContSize?.Size ?? '',
          'Employee Branch': this.selectedEmployeeBranch?.['Branch Code'] ?? '',
          'Employee Department': this.selectedEmployeeDepartment?.ID ?? '',
          'Job System': this.selectedJobSystem ? this.selectedJobSystem['System'] : '',
          'Transport Mode': this.selectedTransMode?.['Trans Mode'] ?? '',

          _action: 'N',
        },
      ];
      this.showTransportChargeTableBox = false; // hide TableBox and re-render on nextTick
      this.transportChargeTableData = [];
      this.transportChargeTableBoxSelectedRow = [];
      this.showTransportChargeInputs = false;
      this.$nextTick(() => {
        this.transportChargeTableData = modifiedTransportChargeTableData;
        this.showTransportChargeTableBox = true; // re-render to force a re-execution of mounted in TableBox, in order to update _rowid
      });
    },
    editTransportChargeInputButtonClicked: function (_pointerEvent) {
      const isInvalid = this.checkTransportChargeFields();
      if (isInvalid) {
        return;
      }

      let matchedIndex; // find the row being edited in the tablebox data
      if (this.transportChargeBeingEdited.Seq !== undefined && this.transportChargeBeingEdited.Seq !== null) {
        // if Seq exists, strict comparison with undefined and null because Seq might be 0
        const foundIndex = this.transportChargeTableData.findIndex(
          (transportCharge) => transportCharge.Seq === this.transportChargeBeingEdited.Seq,
        );
        if (foundIndex !== -1) {
          // if a match was found with Seq
          matchedIndex = foundIndex; // assign the matching index
        }
      }
      if (matchedIndex === undefined && this.transportChargeBeingEdited._rowid) {
        // if Seq does not exist or could not find a match with Seq, and _rowid exists
        const foundIndex = this.transportChargeTableData.findIndex(
          (transportCharge) => transportCharge._rowid === this.transportChargeBeingEdited._rowid,
        ); // try matching with _rowid instead
        if (foundIndex !== -1) {
          // if a match was found with _rowid
          matchedIndex = foundIndex; // assign the matching index
        }
      }
      if (matchedIndex === undefined) {
        // if a match still can't be found, which shouldn't happen
        throw new Error('ChargeCode - Could not find the row being edited');
      }

      this.transportChargeTableData.splice(matchedIndex, 1, {
        // populate "Charge Code" later when actually saving instead, use input value from common field
        // this way, won't have to prevent the user from entering charge code first before creating a new transport charge
        'GL Acc. Code': this.selectedTransportChargeGLAccCode['Acc No'],
        'Delivery Type': this.selectedDeliveryType?.['Delivery Type'] ?? '',
        'Zone Type': this.selectedZoneType?.['Zone Type'] ?? '',
        'Vehicle No': this.selectedVehicleNo?.['Lorry No'] ?? '',
        Txn: this.selectedTxnType?.['GL Txn Type']?.substring?.(0, 1) ?? '',
        Seq: this.transportChargeBeingEdited?.Seq ?? '',
        'Default GL Acc. Code': this.selectedDefaultGLAccCode['Acc No'],
        'Income Acc. Code': this.selectedIncAccCode?.['Acc No'] ?? '',
        'Haulier Code': this.selectedHaulierCode?.['Haulier Code'] ?? '',
        'Job Type': this.selectedJobType?.JobType ?? '',
        'Cont. Size': this.selectedContSize?.Size ?? '',
        'Employee Branch': this.selectedEmployeeBranch?.['Branch Code'] ?? '',
        'Employee Department': this.selectedEmployeeDepartment?.ID ?? '',
        'Job System': this.selectedJobSystem?.System ?? '',
        'Transport Mode': this.selectedTransMode?.['Trans Mode'] ?? '',

        _action: this.transportChargeBeingEdited._action === 'N' ? 'N' : 'E', // if editing a new transport charge, leave as new, else set as edit
      });

      const modifiedTransportChargeTableData = this.transportChargeTableData.map((transportCharge) => {
        delete transportCharge._rowid;
        return transportCharge;
      });
      this.showTransportChargeTableBox = false; // hide TableBox and re-render on nextTick
      this.transportChargeTableData = [];
      this.transportChargeTableBoxSelectedRow = [];
      this.transportChargeBeingEdited = null;
      this.showTransportChargeInputs = false;
      this.$nextTick(() => {
        this.transportChargeTableData = modifiedTransportChargeTableData;
        this.showTransportChargeTableBox = true; // re-render to force a re-execution of mounted in TableBox, in order to update _rowid
      });
    },
    cancelTransportChargeInputButtonClicked: function (_pointerEvent) {
      this.transportChargeBeingEdited = null;
      this.showTransportChargeInputs = false;
    },
    checkTransportChargeFields: function () {
      let error = false;

      if (this.selectedTransportChargeGLAccCode === null) {
        this.transportChargeGLAccCodeErr = 'GL Acc. Code is required!';
        error = true;
      }

      if (this.selectedDefaultGLAccCode === null) {
        this.defaultGLAccCodeErr = 'Default GL Acc. Code is requried!';
        error = true;
      }

      return error;
    },
    jobTypeSelected: function (jobType) {
      if (jobType) {
        this.selectedJobSystem = this.jobSystemListMaster.find((j) => j['System'] == jobType['System']);
      }
    },

    addEMSChargeButtonClicked: function (_pointerEvent) {
      this.clearEMSChargeInputs();
      this.showEMSChargeInputs = true;
    },
    editEMSChargeButtonClicked: function (_buttonText, rowClicked) {
      this.EMSChargeBeingEdited = {
        ...rowClicked,
      };
      this.clearEMSChargeInputs();
      this.populateEMSChargeInputs(rowClicked);
      this.showEMSChargeInputs = true;
    },
    deleteEMSChargeButtonClicked: async function (_pointerEvent) {
      if (this.EMSChargeTableBoxSelectedRow.length === 0) {
        this.$showAlert('error', 'No EMS Charge Selected', 'Please select an EMS charge to delete', [{ text: 'OK' }]);
        return;
      }

      const deleteConfirmation = await this.$showAlert(
        'warning',
        'Delete EMS Charge',
        'Are you sure you wish to delete the selected EMS charge?',
        [{ text: 'Delete', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
      if (deleteConfirmation === 'Cancel') {
        return;
      }

      const EMSChargeToDelete = this.EMSChargeTableBoxSelectedRow[0];
      let matchedIndex; // find the row being deleted in the tablebox data
      if (EMSChargeToDelete.Seq !== undefined && EMSChargeToDelete.Seq !== null) {
        // if Seq exists, strict comparison with undefined and null because Seq might be 0
        const foundIndex = this.EMSChargeTableData.findIndex((EMSCharge) => EMSCharge.Seq === EMSChargeToDelete.Seq);
        if (foundIndex !== -1) {
          // if a match was found with Seq
          matchedIndex = foundIndex; // assign the matching index
        }
      }
      if (matchedIndex === undefined && EMSChargeToDelete._rowid) {
        // if Seq does not exist or could not find a match with Seq, and _rowid exists
        const foundIndex = this.EMSChargeTableData.findIndex(
          (EMSCharge) => EMSCharge._rowid === EMSChargeToDelete._rowid,
        ); // try matching with _rowid instead
        if (foundIndex !== -1) {
          // if a match was found with _rowid
          matchedIndex = foundIndex; // assign the matching index
        }
      }
      if (matchedIndex === undefined) {
        // if a match still can't be found, which shouldn't happen
        throw new Error('ChargeCode - Could not find the row being deleted');
      }

      if (EMSChargeToDelete._action === 'N') {
        // if deleting a newly added EMS charge, simply remove the element from the array
        this.EMSChargeTableData.splice(matchedIndex, 1);
      } else {
        // if deleting an existing EMS charge, set the _action to "D" and POST to API
        this.EMSChargeTableData[matchedIndex]._action = 'D';
      }

      const modifiedEMSChargeTableData = this.EMSChargeTableData.map((EMSCharge) => {
        delete EMSCharge._rowid;
        return EMSCharge;
      });
      this.showEMSChargeTableBox = false; // hide TableBox and re-render on nextTick
      this.EMSChargeTableData = [];
      this.EMSChargeTableBoxSelectedRow = [];
      this.$nextTick(() => {
        this.EMSChargeTableData = modifiedEMSChargeTableData;
        this.showEMSChargeTableBox = true; // re-render to force a re-execution of mounted in TableBox, in order to update _rowid
      });
    },
    addEMSChargeInputButtonClicked: function (_pointerEvent) {
      const isInvalid = this.checkEMSChargeFields();
      if (isInvalid) {
        return;
      }

      const modifiedEMSChargeTableData = [
        ...this.EMSChargeTableData.map((EMSCharge) => {
          delete EMSCharge._rowid;
          return EMSCharge;
        }),
        {
          // populate charge code later when actually saving instead, use input value from common field
          // this way, won't have to prevent the user from entering charge code first before creating a new EMS charge
          'GL Acc. Code': this.selectedEMSChargeGLAccCode['Acc No'],
          'Part Type': this.selectedPartType?.['Part Type'] ?? '',
          'Vehicle Code': this.selectedVehicleCode['Vehicle Code'],
          Ratio: this.ratioInputValue.trim(),

          _action: 'N',
        },
      ];
      this.showEMSChargeTableBox = false; // hide TableBox and re-render on nextTick
      this.EMSChargeTableData = [];
      this.EMSChargeTableBoxSelectedRow = [];
      this.showEMSChargeInputs = false;
      this.$nextTick(() => {
        this.EMSChargeTableData = modifiedEMSChargeTableData;
        this.showEMSChargeTableBox = true; // re-render to force a re-execution of mounted in TableBox, in order to update _rowid
      });
    },
    editEMSChargeInputButtonClicked: function () {
      const isInvalid = this.checkEMSChargeFields();
      if (isInvalid) {
        return;
      }

      let matchedIndex; // find the row being editted in the tablebox data
      if (this.EMSChargeBeingEdited.Seq !== undefined && this.EMSChargeBeingEdited.Seq !== null) {
        // if Seq exists, strict comparison with undefined and null because Seq might be 0
        const foundIndex = this.EMSChargeTableData.findIndex(
          (EMSCharge) => EMSCharge.Seq === this.EMSChargeBeingEdited.Seq,
        );
        if (foundIndex !== -1) {
          // if a match was found with Seq
          matchedIndex = foundIndex; // assign the matching index
        }
      }
      if (matchedIndex === undefined && this.EMSChargeBeingEdited._rowid) {
        // if Seq does not exist or could not find a match with Seq, and _rowid exists
        const foundIndex = this.EMSChargeTableData.findIndex(
          (EMSCharge) => EMSCharge._rowid === this.EMSChargeBeingEdited._rowid,
        ); // try matching with _rowid instead
        if (foundIndex !== -1) {
          // if a match was found with _rowid
          matchedIndex = foundIndex; // assign the matching index
        }
      }
      if (matchedIndex === undefined) {
        // if a match still can't be found, which shouldn't happen
        throw new Error('ChargeCode - Could not find the row being editted');
      }

      this.EMSChargeTableData.splice(matchedIndex, 1, {
        // populate "Charge Code" later when actually saving instead, use input value from common field
        // this way, won't have to prevent the user from entering charge code first before creating a new EMS charge
        'GL Acc. Code': this.selectedEMSChargeGLAccCode['Acc No'],
        'Part Type': this.selectedPartType?.['Part Type'] ?? '',
        'Vehicle Code': this.selectedVehicleCode['Vehicle Code'],
        Ratio: this.ratioInputValue.trim(),
        Seq: this.EMSChargeBeingEdited?.Seq ?? '',

        _action: this.EMSChargeBeingEdited._action === 'N' ? 'N' : 'E', // if editing a new EMS charge, leave as new, else set as edit
      });

      const modifiedEMSChargeTableData = this.EMSChargeTableData.map((EMSCharge) => {
        delete EMSCharge._rowid;
        return EMSCharge;
      });
      this.showEMSChargeTableBox = false; // hide and re-render TableBox on nextTick
      this.EMSChargeTableData = [];
      this.EMSChargeTableBoxSelectedRow = [];
      this.EMSChargeBeingEdited = null;
      this.showEMSChargeInputs = false;
      this.$nextTick(() => {
        this.EMSChargeTableData = modifiedEMSChargeTableData;
        this.showEMSChargeTableBox = true; // re-render to force a re-execution of mounted in TableBox, in order to update _rowid
      });
    },
    cancelEMSChargeInputButtonClicked: function (_pointerEvent) {
      this.EMSChargeBeingEdited = null;
      this.showEMSChargeInputs = false;
    },
    checkEMSChargeFields: function () {
      let error = false;

      if (this.selectedEMSChargeGLAccCode === null) {
        this.EMSChargeGLAccCodeErr = 'GL Acc. Code is required!';
        error = true;
      }

      if (this.selectedVehicleCode === null) {
        this.vehicleCodeErr = 'Vehicle Code is requried!';
        error = true;
      }

      if (this.ratioInputValue.trim() === '') {
        this.ratioErr = 'Ratio is required!';
        error = true;
      }

      return error;
    },
    clearEMSChargeInputs: function () {
      this.selectedEMSChargeGLAccCode = null;
      this.EMSChargeGLAccCodeErr = '';
      this.selectedVehicleCode = null;
      this.vehicleCodeErr = '';
      this.ratioInputValue = '';
      this.ratioErr = '';
      this.selectedPartType = null;
    },
    populateEMSChargeInputs: function (EMSChargeDetails) {
      this.selectedEMSChargeGLAccCode = this.populateObject(
        this.GLAccListMaster,
        'Acc No',
        EMSChargeDetails['GL Acc. Code'],
      );
      this.selectedVehicleCode = this.populateObject(
        this.lorryNoListMaster,
        'Vehicle Code',
        EMSChargeDetails['Vehicle Code'],
      );
      this.ratioInputValue = this.populateTextTrim(EMSChargeDetails['Ratio']);
      this.selectedPartType = this.populateObject(
        this.EMSPartTypeListMaster,
        'Part Type',
        EMSChargeDetails['Part Type'],
      );
    },

    tabClicked: async function (_tabItem, tabIndex) {
      if (this.showTransportChargeInputs && tabIndex !== 1) {
        this.$nextTick(() => {
          // needs to be in nextTick otherwise the prop only updates after the assignment, which cause the tab to still switch even though there are unsaved changes
          this.currentTab = 1; // change back to previous tab
        });

        const discardChanges = await this.promptDiscardTransportChargeChanges();
        if (discardChanges === 'Cancel') {
          return;
        }

        this.showTransportChargeInputs = false; // if user wishes to discard changes, hide inputs
        this.transportChargeBeingEdited = null; // set to null just in case
      }

      if (this.showEMSChargeInputs && tabIndex !== 2) {
        this.$nextTick(() => {
          // needs to be in nextTick otherwise the prop only updates after the assignment, which cause the tab to still switch even though there are unsaved changes
          this.currentTab = 2; // change back to previous tab
        });

        const discardChanges = await this.promptDiscardEMSChargeChanges();
        if (discardChanges === 'Cancel') {
          return;
        }

        this.showEMSChargeInputs = false; // if user wishes to discard changes, hide inputs
        this.EMSChargeBeingEdited = null; // set to null just in case
      }
      this.currentTab = tabIndex; // change to the tab user clicked on
    },
    promptDiscardTransportChargeChanges: function () {
      // $showAlert returns a promise, which can be awaited in the calling function to get the button clicked by the user
      return this.$showAlert(
        'warning',
        'Discard Changes',
        'You have unsaved transport charge changes.<br>Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },
    promptDiscardEMSChargeChanges: function () {
      // $showAlert returns a promise, which can be awaited in the calling function to get the button clicked by the user
      return this.$showAlert(
        'warning',
        'Discard Changes',
        'You have unsaved EMS charge changes.<br>Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },

    checkPopupEntryFields: function () {
      let error = false;

      this.clearPopupEntryMessages();
      if (this.chargeCodeInputValue.trim() === '') {
        this.chargeCodeErr = 'Charge Code is required!';
        this.popupEntryRequiredError.push({ key: 'chargeCodeErr', value: 'Charge Code is required!' });
        error = true;
      } else if (this.chargeCodeInputValue.length > 10) {
        this.chargeCodeErr = 'Charge Code cannot exceed 10 characters!';
        this.popupEntryRequiredError.push({ key: 'chargeCodeErr', value: 'Charge Code cannot exceed 10 characters!' });
        error = true;
      }

      if (this.description.trim() === '') {
        this.descriptionErr = 'Description is required!';
        this.popupEntryRequiredError.push({ key: 'descriptionErr', value: 'Description is required!' });
        error = true;
      } else if (this.description.length > 100) {
        this.descriptionErr = 'Description cannot exceed 100 characters!';
        this.popupEntryRequiredError.push({
          key: 'descriptionErr',
          value: 'Description cannot exceed 100 characters!',
        });
        error = true;
      }

      if (!this.selectedPayable) {
        this.payableErr = 'Payable is required!';
        this.popupEntryRequiredError.push({ key: 'payableErr', value: 'Payable is required!' });
        error = true;
      }
      if (!this.selectedBillable) {
        this.billableErr = 'Billable is required!';
        this.popupEntryRequiredError.push({ key: 'billableErr', value: 'Billable is required!' });
        error = true;
      }
      if (this.selectedPayable?.label === 'No' && this.selectedBillable?.label === 'No') {
        this.popupEntryRequiredError.push({
          key: 'payableErr',
          value: 'Charge Code cannot be both not payable and not billable!',
        }); // focus on payable RadioButtonSet when clicked
        error = true;
      }
      if (!this.selectedARGrouping) {
        this.ARGroupingErr = 'AR Grouping is required!';
        this.popupEntryRequiredError.push({ key: 'ARGroupingErr', value: 'AR Grouping is required!' });
        error = true;
      }
      if (!this.selectedB2BCharge) {
        this.B2BChargeErr = 'Is B2B Charge is required!';
        this.popupEntryRequiredError.push({ key: 'B2BChargeErr', value: 'Is B2B Charge is required!' });
        error = true;
      }
      
      if (this.chargeCodeInputValue != this.selectedGroupCodeFreeText && !this.selectedGroupCode) {
        this.selectedGroupCodeErr = 'Group Code is required!';
        this.popupEntryRequiredError.push({ key: 'selectedGroupCodeErr',  value: 'Group Code is required!'});
        error = true;
      }
      
      if (this.SSTRateInputValue.length > 3) {
        // an optional field
        this.SSTRateErr = 'SST Rate cannot exceed 3 digits!';
        this.popupEntryRequiredError.push({ key: 'SSTRateErr', value: 'SST Rate cannot exceed 3 digits!' });
        error = true;
      }

      if (this.ARGLAccCodeIsRequired && !this.selectedARGLAccCode) {
        this.ARGLAccCodeErr = 'AR GL Acc. Code is required!';
        this.popupEntryRequiredError.push({ key: 'ARGLAccCodeErr', value: 'AR GL Acc. Code is required!' });
        error = true;
      }

      if (this.APGLAccCodeIsRequired && !this.selectedAPGLAccCode) {
        this.APGLAccCodeErr = 'AP GL Acc. Code is required!';
        this.popupEntryRequiredError.push({ key: 'APGLAccCodeErr', value: 'AP GL Acc. Code is required!' });
        error = true;
      }

      if (this.expensesIsRequired && !this.selectedExpenses) {
        this.expensesErr = 'Expenses is required!';
        this.popupEntryRequiredError.push({ key: 'expensesErr', value: 'Expenses is required!' });
        error = true;
      }

      return error;
    },
    popupEntrySaveClicked: async function () {
      if (this.showTransportChargeInputs) {
        const discardChanges = await this.promptDiscardTransportChargeChanges();
        if (discardChanges === 'Cancel') {
          return;
        }

        this.showTransportChargeInputs = false;
        this.transportChargeBeingEdited = null; // set to null just in case
      }

      if (this.showEMSChargeInputs) {
        const discardChanges = await this.promptDiscardEMSChargeChanges();
        if (discardChanges === 'Cancel') {
          return;
        }

        this.showEMSChargeInputs = false;
        this.EMSChargeBeingEdited = null; // set to null just in case
      }

      const isInvalid = this.checkPopupEntryFields();
      if (isInvalid) {
        return;
      }

      const action = this.popupEntryTitle[0]; // first character
      const requestBody = this.formatSaveChargeCodeRequestBody(action);

      this.$fullscreenLoading('Saving Charge Code');
      const result = await this.$apiCallV2('/ChargeCode/SaveChargeCode', requestBody);

      if (result[0]['Results'] != '0') {
        // Save error
        if (action == 'N') {
          this.popupEntryErrorMessages.push(`There was an error creating Charge Code: ${result[0]['ErrMsg']}`);
        } else {
          this.popupEntryErrorMessages.push(`There was an error editing Charge Code: ${result[0]['ErrMsg']}`);
        }

        this.$fullscreenLoading();
        return;
      }

      // Save successful
      this.viewMode = true;
      this.viewModePK = true;
      this.popupEntryTitle = 'View Charge Code';
      this.showTransportChargeTableBox = false;
      this.showEMSChargeTableBox = false;
      this.$nextTick(() => {
        // hide and re-show TableBoxes to trigger a re-execution of TableBox's mounted, so that the additional buttons get hidden
        this.showTransportChargeTableBox = true;
        this.showEMSChargeTableBox = true;
      });

      this.$fullscreenLoading('Refreshing Charge Code');
      await this.getChargeCodeListMaster(); // refresh Charge Code TableBox

      this.$fullscreenLoading('Getting Charge Code Details');
      const chargeCodeDetails = await this.getChargeCodeDetails(requestBody.chargecode);
      this.populatePopupEntryFields(chargeCodeDetails);

      if (action == 'N') {
        this.popupEntrySuccessMessages.push(`Successfully created Charge Code: ${this.chargeCodeInputValue}`);
      } else {
        this.popupEntrySuccessMessages.push(`Successfully edited Charge Code: ${this.chargeCodeInputValue}`);
      }

      this.$fullscreenLoading();
    },
    formatSaveChargeCodeRequestBody: function (action) {
      // formats and returns the request body for the SaveChargeCode API endpoint
      // reads all values needed from the PopupEntry input field values
      // populate PopupEntry before calling this method

      const requestBody = {
        action: action,
        dateupdate: new Date().formatString('yyyy-MM-dd'),

        chargecode: this.chargeCodeInputValue.trim(),
        active: this.activeIsChecked ? '1' : '0',
        description: this.description,

        payable: this.selectedPayable?.keyValue,
        billable: this.selectedBillable?.keyValue,
        argrouping: this.selectedARGrouping?.keyValue,
        b2b: this.selectedB2BCharge?.keyValue,

        transactioncode: this.selectedGroupCode?.['Charge Code'] ?? this.selectedGroupCodeFreeText,
        uom: this.selectedUnitMeasurement?.['UOM Code'] ?? '',
        codeinexpense: this.selectedCodeInExpenses?.['Charge Code'] ?? '',
        statuscode: this.selectedStatusCode?.Status ?? '',
        chrggroup: this.selectedGroupUnder?.['Charges Group'] ?? '',
        classification: this.selectedClassification?.['Classification Code'] ?? '',
        taxrate: this.SSTRateInputValue,
        sstservicetypecode: this.selectedSSTCode?.['Service Type Code'] ?? '',

        aracccode: this.selectedARGLAccCode?.['Acc No'] ?? '',
        artaxcode: this.selectedGSTSSTARTaxCode?.['Tax Code'] ?? '',
        artaxglaccno: this.selectedGSTSSTARGLAccCode?.['Acc No'] ?? '',
        gstmsic: this.selectedMSICCode?.['MSIC Code'] ?? '',
        apaccno: this.selectedAPGLAccCode?.['Acc No'] ?? '',
        aptaxcode: this.selectedGSTAPTaxCode?.['Tax Code'] ?? '',
        aptaxglaccno: this.selectedGSTAPGLAccCode?.['Acc No'] ?? '',

        exptype: this.selectedExpenses?.keyValue ?? '',

        // transport and ems arrays populated below
      };

      if (action === 'D') {
        // if action is delete
        return requestBody; // don't need to format the body for transport charge and EMS charge
      }

      const transportArray = [];
      for (const transportCharge of this.transportChargeTableData) {
        if (!transportCharge._action) {
          continue;
        }

        transportArray.push({
          action: transportCharge._action,
          seq: transportCharge.Seq ?? '', // won't have a value if it's a new transport charge

          chargecode: this.chargeCodeInputValue.trim(), // populate charge code using common field input value, instead of using value from column
          // this way, won't have to prevent the user from entering charge code first before creating a new transport charge
          glcode: transportCharge['GL Acc. Code'],
          defaultglcode: transportCharge['Default GL Acc. Code'],
          incomeacccode: transportCharge['Income Acc. Code'],
          vehicleno: transportCharge['Vehicle No'],
          txntype: transportCharge['Txn'],
          jobtype: transportCharge['Job Type'],
          zonetype: transportCharge['Zone Type'],
          hauliercode: transportCharge['Haulier Code'],
          contsize: transportCharge['Cont. Size'],
          deliverytype: transportCharge['Delivery Type'],
          employeebranch: transportCharge['Employee Branch'],
          employeedepartment: transportCharge['Employee Department'],
          jobsystem: transportCharge['Job System'],
          transportmode: transportCharge['Transport Mode'],
        });
      }
      requestBody.transport = transportArray;

      const EMSArray = [];
      for (const EMSCharge of this.EMSChargeTableData) {
        if (!EMSCharge._action) {
          continue;
        }

        EMSArray.push({
          action: EMSCharge._action,
          seq: EMSCharge.Seq ?? '', // won't have a seq number if it's a new EMS charge
          chargecode: this.chargeCodeInputValue.trim(), // populate charge code using common field input value, instead of using value from column
          // this way, won't have to prevent the user from entering charge code first before creating an EMS charge
          glcode: EMSCharge['GL Acc. Code'],
          vehiclecode: EMSCharge['Vehicle Code'],
          ratio: EMSCharge['Ratio'],
          parttype: EMSCharge['Part Type'],
        });
      }
      requestBody.ems = EMSArray;

      return requestBody;
    },
    popupEntryRequiredClicked: function (item) {
      const errorTabs = this.errorTabMapping.find((e) => e['key'] == item);
      if (this.currentTab != errorTabs['tab'] && errorTabs['tab'] >= 0) {
        this.currentTab = errorTabs['tab'];
      }
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },

    populateTextTrim: function (text) {
      // To populate text with trim
      return text ? text.trim() : '';
    },
    populateObject: function (data, key, compare) {
      // To populate object into dropdown or input query
      try {
        return data.find((d) => (d[key]?.trim?.() ?? d[key]) == (compare?.trim?.() ?? compare));
      } catch (error) {
        return null;
      }
    },
    chargeCodeChange: function(focus) {
      this.$nextTick(() => {
        if (!focus && !this.selectedGroupCode && this.chargeCodeInputValue !== '') {
            this.selectedGroupCodeFreeText = this.chargeCodeInputValue;
        }
      });
    },
  },
  mounted() {
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Charge Code') {
        x.data.forEach((k) => {
          if (k.Control === 'mnuSettings' && k.Properties === 'Visible') {
            this.SASShowActive = k.PropertiesValue === 'True';
            return;
          }

          //key in menu same as control value from api
          const selectedMenu = this.menu.find((m) => m['key'] == k['Control']);
          if (selectedMenu) {
            selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] == 'True';
          } else {
            const selectedTab = this.tabItems.find((t) => t['key'] == k['Control']);
            if (selectedTab) {
              selectedTab['disabled'] = k['PropertiesValue'] != 'True';
            }
          }
        });
      }
    });

    const mountedPromises = [];
    let promisesFulfilled = 0;

    mountedPromises.push(
      this.getChargeCodeListMaster().then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'UOM' }).then((result) => {
        this.UOMListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'MSIC' }).then((result) => {
        this.MSICData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'STATUS' }).then((result) => {
        this.statusListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CHARGESCODEGROUP' }).then((result) => {
        this.chargeCodeGroupListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CHARGESCODECLASS' }).then((result) => {
        this.chargeCodeClassListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GLACCOUNT' }).then((result) => {
        this.GLAccListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'ARTAXCODE' }).then((result) => {
        this.ARTaxCodeListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'APTAXCODE' }).then((result) => {
        this.APTaxCodeListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GLTXNTYPE' }).then((result) => {
        this.GLTxnTypeListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'LORRYNO' }).then((result) => {
        this.lorryNoListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'EMSPARTTYPE' }).then((result) => {
        this.EMSPartTypeListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'JOBTYPE' }).then((result) => {
        this.jobTypeListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'ZONE' }).then((result) => {
        this.zoneListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'HAULIER' }).then((result) => {
        this.haulierListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CONTSIZE' }).then((result) => {
        this.contSizeListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CONTDELIVERYTYPE' }).then((result) => {
        this.contDeliveryTypeListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'BRANCH' }).then((result) => {
        this.branchListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'DEPARTMENT' }).then((result) => {
        this.departmentListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SSTSERVICETYPE' }).then((result) => {
        this.sstServiceTypeCodeListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SYSTEM' }).then((result) => {
        this.jobSystemListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'TRANSMODE' }).then((result) => {
        this.transportModeListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    Promise.allSettled(mountedPromises).then((_promiseOutcomes) => {
      this.menu = this.menu.map((menu) => ({
        ...menu,
        enabled: true,
      }));
    });
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.charge-code-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .table-box-container {
    width: 100%;
    margin-left: 5px;
    padding: 5px;
    background-color: white;
    border-radius: 5px;
    display: flex;
    overflow: hidden;
  }

  .popup-entry-component {
    .general-inputs {
      display: flex;
      flex-direction: column;

      > .charge-code-and-active {
        display: flex;
        flex-direction: row;

        > .charge-code-input {
          margin-right: 3px;
        }

        > .active-check-box {
          margin-left: 3px;
        }
      }
    }

    .tab-component {
      height: calc(100% - 60px);

      .job-charge-code {
        display: flex;
        flex-direction: column;

        > .charge-type-radio-buttons {
          display: flex;
          flex-direction: row;

          > .payable-and-billable {
            display: flex;
            flex-direction: column;
            width: 140px;
            margin-right: 10px;

            > .radio-button-set-component:nth-child(2) {
              margin-top: 5px;
            }
          }

          > .ar-grouping-and-b2b {
            display: flex;
            flex-direction: column;
            width: 150px;

            > .radio-button-set-component:nth-child(2) {
              margin-top: 5px;
            }
          }
        }

        > .charge-details-inputs {
          display: flex;
          flex-direction: row;
          margin-top: 5px;

          > .inputs-column {
            display: flex;
            flex-direction: column;
            width: 50%; // use width: 50% instead of flex: 1 with overflow: hidden, because overflow: hidden will hide the dropdown when expanded

            > .input-component {
              width: 50%;
            }
          }
        }

        > .expenses-radio-button {
          display: flex;
        }
      }

      .transport-charge {
        display: flex;
        flex-direction: row;
        height: 100%;

        > .transport-charge-list {
          display: flex;
          flex-direction: column;
          flex: 1;
          overflow: hidden;
          margin-right: 3px;

          > .call-to-action-buttons {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            margin-bottom: 2px;

            > button:nth-child(1) {
              margin-right: 5px;
            }
          }

          > .table-box-container {
            overflow: hidden;
          }
        }

        > .transport-charge-inputs {
          display: flex;
          flex-direction: column;
          height: 100%; // set height and width properties instead of flex: 1 with overflow: hidden, because the former will cut off the dropdown list at the last input
          width: 50%; // leaving .transport-charge-list with flex: 1 and overflow: hidden won't cause any issues, and removes the need to change the width based on whether the inputs are shown or not
          margin-left: 3px;

          > .call-to-action-buttons {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;

            > button:nth-child(1) {
              margin-right: 5px;
            }
          }

          > .inputs {
            height: 100%; // set height property, otherwise the dropdown list will get cut off at the last input
            overflow-y: auto;

            > .dropdown-component {
              width: 50%;
            }
          }
        }
      }

      .ems-charge {
        display: flex;
        flex-direction: row;
        height: 100%;

        > .ems-charge-list {
          display: flex;
          flex-direction: column;
          flex: 1;
          overflow: hidden;
          margin-right: 3px;

          > .call-to-action-buttons {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            margin-bottom: 2px;

            > button:nth-child(1) {
              margin-right: 5px;
            }
          }

          > .table-box-container {
            overflow: hidden;
          }
        }

        > .ems-charge-input {
          display: flex;
          flex-direction: column;
          height: 100%; // set height and width properties instead of flex: 1 with overflow: hidden, because the former will cut off the dropdown list at the last input
          width: 50%; // leaving .ems-charge-list with flex: 1 and overflow: hidden won't cause any issues, and removes the need to change the width based on whether the inputs are shown or not
          margin-left: 3px;

          > .call-to-action-buttons {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;

            > button:nth-child(1) {
              margin-right: 5px;
            }
          }

          > .inputs {
            height: 100%; // set height property, otherwise the dropdown list will get cut off at the last input
            overflow-y: auto;

            > .input-component {
              width: 50%;
            }
          }
        }
      }
    }
  }
}
</style>
